import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@volca/ui/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@volca/ui/components/ui/select";
import type {
  SelectedWorkbookExport,
  WorkbookExportFormat,
  WorkbookExportSheet,
} from "./types";
import { WorkbookSheetSelection } from "./workbook-sheet-selection";

const EXPORT_SCOPE = { full: "full", selected: "selected" } as const;

const FORMAT_LABELS: Record<WorkbookExportFormat, string> = {
  xlsx: "Excel (.xlsx)",
  csv: "CSV (.csv)",
};

interface WorkbookExportDialogProps {
  sheets: WorkbookExportSheet[];
  isDownloading: boolean;
  isPreparing: boolean;
  isComplete: boolean;
  error?: string;
  onClose: () => void;
  onExport: (options?: SelectedWorkbookExport) => void;
}

export const WorkbookExportDialog = ({
  sheets,
  isDownloading,
  isPreparing,
  isComplete,
  error,
  onClose,
  onExport,
}: WorkbookExportDialogProps) => {
  const [selectedIds, setSelectedIds] = useState(() =>
    sheets.map((sheet) => sheet.id),
  );
  const [selectedSheetsOnly, setSelectedSheetsOnly] = useState(false);
  const [format, setFormat] = useState<WorkbookExportFormat>("xlsx");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const availableIds = new Set(sheets.map((sheet) => sheet.id));
  const visibleIds = selectedIds.filter((id) => availableIds.has(id));
  const exportFormat = visibleIds.length === 1 ? format : "xlsx";
  const isSelectionEmpty = selectedSheetsOnly && visibleIds.length === 0;
  const changeSelection = (ids: string[]) => {
    setSelectedIds(ids);
    if (ids.length !== 1) setFormat("xlsx");
    setHasSubmitted(false);
  };
  const handleExport = () => {
    if (isDownloading || isSelectionEmpty) return;
    setHasSubmitted(true);
    if (selectedSheetsOnly) {
      onExport({ sheetIds: visibleIds, exportFormat });
    } else {
      onExport();
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogDescription className="sr-only">
          Choose how to download this workbook.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle>Download workbook</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-xs">
          <RadioGroup
            aria-label="Download scope"
            className="flex gap-4"
            value={
              selectedSheetsOnly ? EXPORT_SCOPE.selected : EXPORT_SCOPE.full
            }
            disabled={isDownloading}
            onValueChange={(value) => {
              setSelectedSheetsOnly(value === EXPORT_SCOPE.selected);
              setHasSubmitted(false);
            }}
          >
            <label className="flex items-center gap-2">
              <RadioGroupItem value={EXPORT_SCOPE.full} />
              Full workbook
            </label>
            <label className="flex items-center gap-2">
              <RadioGroupItem value={EXPORT_SCOPE.selected} />
              Selected sheets
            </label>
          </RadioGroup>
          {selectedSheetsOnly && (
            <>
              <WorkbookSheetSelection
                sheets={sheets}
                selectedIds={visibleIds}
                disabled={isDownloading}
                onChange={changeSelection}
              />
              <div className="flex items-center gap-3">
                <span id="workbook-export-format-label">File format</span>
                <Select
                  value={exportFormat}
                  items={FORMAT_LABELS}
                  disabled={isDownloading}
                  onValueChange={(value) => {
                    setFormat(value === "csv" ? "csv" : "xlsx");
                    setHasSubmitted(false);
                  }}
                >
                  <SelectTrigger
                    aria-labelledby="workbook-export-format-label"
                    className="w-40"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="xlsx">{FORMAT_LABELS.xlsx}</SelectItem>
                      <SelectItem
                        value="csv"
                        disabled={visibleIds.length !== 1}
                      >
                        {FORMAT_LABELS.csv}
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-subtle">
                Select one sheet to use CSV. Selected exports contain calculated
                values, without formulas.
              </p>
            </>
          )}
          {!selectedSheetsOnly && (
            <p className="text-subtle">
              Download all sheets as Excel (.xlsx), with formulas.
            </p>
          )}
          {isSelectionEmpty && sheets.length > 0 && (
            <p role="status">Select at least one sheet.</p>
          )}
          {isDownloading && (
            <p role="status">
              {isPreparing
                ? "Preparing your file. The download will start automatically."
                : "Starting download…"}
            </p>
          )}
          {hasSubmitted && error && (
            <p role="alert" className="text-danger">
              {error}
            </p>
          )}
          {hasSubmitted && isComplete && !isDownloading && (
            <p role="status">Your file has downloaded.</p>
          )}
        </div>
        <DialogFooter>
          <Button size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            size="sm"
            disabled={isDownloading || isSelectionEmpty}
            onClick={handleExport}
            loading={isDownloading}
          >
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
